import React, { createContext, useContext, useMemo } from "react"

/**
 * RunwayContext exposes the four canonical buckets every Runway component is
 * allowed to read from:
 *
 *   ctx     — identity + routing primitives (graphName, runId, mode, …)
 *   config  — the per-component manifest config blob
 *   io      — imperative actions (patch draft, start run, upload file, …)
 *   events  — reactive subscriptions (run, node progress, executor events)
 *
 * Components SHOULD NOT call API endpoints directly: every interaction goes
 * through `io` so the shell can swap implementations (test mocks, a future
 * IndexedDB queue, etc.) without touching consumers.
 */
const RunwayContextValue = createContext(null)

export function RunwayContextProvider({ value, children }) {
  // Memoise so consumers don't re-render unless one of the buckets changes.
  const stable = useMemo(() => ({
    ctx: value?.ctx || {},
    config: value?.config || {},
    io: value?.io || {},
    events: value?.events || {},
  }), [value?.ctx, value?.config, value?.io, value?.events])
  return <RunwayContextValue.Provider value={stable}>{children}</RunwayContextValue.Provider>
}

export function useRunway() {
  const v = useContext(RunwayContextValue)
  if (!v) throw new Error("useRunway() must be used inside <RunwayContextProvider>")
  return v
}

export function useRunwayCtx() { return useRunway().ctx }
export function useRunwayConfig() { return useRunway().config }
export function useRunwayIo() { return useRunway().io }
export function useRunwayEvents() { return useRunway().events }
